import {
  classifyPageHealth,
  effectiveHealthSortRank,
  healthBandSortRank,
  resolveEffectiveHealthBand,
  secondaryHealthSortRank,
  type InventoryHealthFilterBand,
  type PageHealthBand,
  type PageRealizationSnapshot,
  type SecondaryPageHealthSnapshot
} from './inventory-page-health';
import {
  realizationGapSortValue,
  realizationRatioSortValue,
  serpKeywordDifficultySortValue
} from './inventory-realization-sort';

export type InventoryClientViewSortField =
  | 'analyzedAt'
  | 'onpageScore'
  | 'wordCount'
  | 'statusCode'
  | 'health'
  | 'secondaryHealth'
  | 'effectiveHealth'
  | 'realizationGap'
  | 'realizationRatio'
  | 'serpKeywordDifficulty'
  | 'estimatedOrganicTraffic'
  | 'topicWeight'
  | 'topicRelevanceScore'
  | 'url';

export interface InventoryClientViewPage {
  urlId: string;
  url: string;
  analyzedAt: string;
  onpageScore: number;
  wordCount: number;
  statusCode: number;
  estimatedOrganicTraffic: number | null;
  topicWeight: number | null;
  topicRelevanceScore: number | null;
  effectiveHealthBand?: InventoryHealthFilterBand | null;
  pageHealth?: { band: PageHealthBand } | null;
  pageSecondaryHealth?: SecondaryPageHealthSnapshot | null;
  pageRealization?: PageRealizationSnapshot | null;
  serpKeywordDifficulty?: number | null;
}

export interface InventoryClientViewPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export function inventoryPageEffectiveHealthBand(page: InventoryClientViewPage): InventoryHealthFilterBand {
  if (page.effectiveHealthBand) return page.effectiveHealthBand;
  const primaryBand = page.pageHealth?.band ?? classifyPageHealth({
    statusCode: page.statusCode,
    estimatedOrganicTraffic: page.estimatedOrganicTraffic
  }).band;
  return resolveEffectiveHealthBand(primaryBand, page.pageSecondaryHealth || null);
}

export function filterInventoryPagesByHealthBands<T extends InventoryClientViewPage>(
  pages: T[],
  bands: InventoryHealthFilterBand[]
): T[] {
  if (bands.length === 0) return [...pages];
  const allowed = new Set(bands);
  return pages.filter((page) => allowed.has(inventoryPageEffectiveHealthBand(page)));
}

export function compareInventoryPagesForClientView(
  a: InventoryClientViewPage,
  b: InventoryClientViewPage,
  sortBy: InventoryClientViewSortField,
  sortOrder: 'asc' | 'desc'
): number {
  const order = sortOrder === 'asc' ? 1 : -1;

  if (sortBy === 'health') {
    const primaryA = a.pageHealth?.band ?? classifyPageHealth({
      statusCode: a.statusCode,
      estimatedOrganicTraffic: a.estimatedOrganicTraffic
    }).band;
    const primaryB = b.pageHealth?.band ?? classifyPageHealth({
      statusCode: b.statusCode,
      estimatedOrganicTraffic: b.estimatedOrganicTraffic
    }).band;
    const diff = healthBandSortRank(primaryB) - healthBandSortRank(primaryA);
    return order === 1 ? -diff : diff;
  }

  if (sortBy === 'secondaryHealth') {
    const diff = secondaryHealthSortRank(b.pageSecondaryHealth || null) -
      secondaryHealthSortRank(a.pageSecondaryHealth || null);
    return order === 1 ? -diff : diff;
  }

  if (sortBy === 'effectiveHealth') {
    const diff = effectiveHealthSortRank(inventoryPageEffectiveHealthBand(b)) -
      effectiveHealthSortRank(inventoryPageEffectiveHealthBand(a));
    return order === 1 ? -diff : diff;
  }

  if (sortBy === 'realizationGap') {
    const diff = realizationGapSortValue(b.pageRealization) - realizationGapSortValue(a.pageRealization);
    return order === 1 ? -diff : diff;
  }

  if (sortBy === 'realizationRatio') {
    const diff = realizationRatioSortValue(b.pageRealization) - realizationRatioSortValue(a.pageRealization);
    return order === 1 ? -diff : diff;
  }

  if (sortBy === 'serpKeywordDifficulty') {
    const diff =
      serpKeywordDifficultySortValue(b.serpKeywordDifficulty) -
      serpKeywordDifficultySortValue(a.serpKeywordDifficulty);
    return order === 1 ? diff : -diff;
  }

  if (sortBy === 'url') {
    const cmp = String(a.url || '').localeCompare(String(b.url || ''));
    return order === 1 ? cmp : -cmp;
  }

  if (sortBy === 'analyzedAt') {
    const cmp = new Date(b.analyzedAt).getTime() - new Date(a.analyzedAt).getTime();
    return order === 1 ? -cmp : cmp;
  }

  const numericValue = (page: InventoryClientViewPage, field: InventoryClientViewSortField): number => {
    if (field === 'onpageScore') return page.onpageScore;
    if (field === 'wordCount') return page.wordCount;
    if (field === 'statusCode') return page.statusCode;
    if (field === 'estimatedOrganicTraffic') {
      return Number.isFinite(page.estimatedOrganicTraffic) ? Number(page.estimatedOrganicTraffic) : 0;
    }
    if (field === 'topicWeight') {
      return Number.isFinite(page.topicWeight) ? Number(page.topicWeight) : 0;
    }
    if (field === 'topicRelevanceScore') {
      return Number.isFinite(page.topicRelevanceScore) ? Number(page.topicRelevanceScore) : 0;
    }
    return 0;
  };
  const cmp = numericValue(b, sortBy) - numericValue(a, sortBy);
  return order === 1 ? -cmp : cmp;
}

export function paginateInventoryClientView<T extends InventoryClientViewPage>(
  pages: T[],
  page: number,
  limit: number
): { pages: T[]; pagination: InventoryClientViewPagination } {
  const total = pages.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * limit;
  return {
    pages: pages.slice(start, start + limit),
    pagination: {
      page: safePage,
      limit,
      total,
      totalPages
    }
  };
}

export function buildInventoryClientView<T extends InventoryClientViewPage>(
  sourcePages: T[],
  options: {
    healthBands: InventoryHealthFilterBand[];
    sortBy: InventoryClientViewSortField;
    sortOrder: 'asc' | 'desc';
    page: number;
    limit: number;
  }
): { pages: T[]; pagination: InventoryClientViewPagination } {
  const filtered = filterInventoryPagesByHealthBands(sourcePages, options.healthBands);
  const sorted = [...filtered].sort((a, b) =>
    compareInventoryPagesForClientView(a, b, options.sortBy, options.sortOrder)
  );
  return paginateInventoryClientView(sorted, options.page, options.limit);
}
